import config from "@/config.js"
import { defineStore } from "pinia"
import { format, addDays } from "date-fns"
import HTTP_STATUS from "@/helpers/httpStatus.js"
import { getCountry, getCity, getPlace } from "@/helpers/alias.js"
import {
  fetchApiJson,
  makeDataFetchCache,
  dataFetch,
  cachedDataFetch,
} from "@/services/repowerAPIService.js"
import useMeteringPointsStore from "@/stores/repower/meteringPointsStore.js"
import useViewStore from "@/stores/viewStore.js"

/* -------------------------------- Constants ------------------------------- */

export const PORTFOLIO = Symbol()
export const ALLOCATION_POINT = Symbol()
export const ASSET = Symbol()

export const ASSET_TYPE = {
  SOLAR_PARK: "SolarPark",
  WIND_PARK: "WindPark",
  BATTERY: "Battery",
  CONSUMER: "Consumer",
  CHARGING_STATION: "ChargingStation",
}

export const ASSET_TYPE_LABELS = {
  [ASSET_TYPE.SOLAR_PARK]: "Zonnepark",
  [ASSET_TYPE.WIND_PARK]: "Windmolenpark",
  [ASSET_TYPE.BATTERY]: "Batterij",
  [ASSET_TYPE.CONSUMER]: "Verbruik",
  [ASSET_TYPE.CHARGING_STATION]: "Oplaadpunt",
}

export const ASSET_TYPE_HIDDEN_STATUS = [ASSET_TYPE.CONSUMER]
export const ASSET_TYPE_HIDDEN_MEASUREMENTS = [ASSET_TYPE.CONSUMER]

const ASSET_TYPE_ICONS = {
  [ASSET_TYPE.SOLAR_PARK]: "sun",
  [ASSET_TYPE.WIND_PARK]: "windy",
  [ASSET_TYPE.BATTERY]: "battery",
  [ASSET_TYPE.CONSUMER]: "database",
  [ASSET_TYPE.CHARGING_STATION]: "plug",
}

/* --------------------------------- Getters -------------------------------- */

function alias() {
  switch (this.role) {
    case PORTFOLIO:
      return `${getCountry(this.id)} Portfolio`
    case ALLOCATION_POINT:
      return `Locatie ${getCity(this.id)}`
    case ASSET:
      return `${getPlace(this.id)} ${ASSET_TYPE_LABELS[this.type]}`
    default:
      return null
  }
}

function displayName() {
  return useViewStore().anonymize ? this.alias : this.name
}

function queryString() {
  return useMeteringPointsStore().queryStringsByPortfolio[this.id]
}

/* --------------------------------- Helpers -------------------------------- */

/**
 * Fill the metering points with information from the detailed API response.
 */
function fillMeteringPointDetails(meteringPoints, data) {
  for (const mp of meteringPoints) {
    const mpData = data.find((item) => item.id === mp.id)
    if (mpData) {
      mp.setMeteringPoint(mpData)
    } else {
      const error = new Error(
        `Metering point with ID ${mp.id} not found in the response data.`,
      )
      error.code = HTTP_STATUS.CONFLICT
      throw error
    }
  }
}

/* --------------------------------- Actions -------------------------------- */

function setMeteringPoint(asset) {
  this.properties = asset
  this.status = asset.status
}

/**
 * Fetches the detailed information for the assets and allocation points of the
 * portfolio. Updates the metering point stores in place with the fetched data.
 */
function loadPortfolioDetails(onSuccess, onFail) {
  if (this.detailsLoaded) {
    onSuccess?.(this.assets)
    return
  }
  // Chain the details loading to the overview promise to ensure the overview
  // data is available for filling.
  const meteringPointsStore = useMeteringPointsStore()
  const overviewPromise = meteringPointsStore.overviewPromise
  if (overviewPromise === null) {
    const error = new Error("Overview not loaded")
    error.code = HTTP_STATUS.FAILED_DEPENDENCY
    onFail?.(error)
    return
  }
  overviewPromise
    .then(() =>
      // Continu only if both the asset and allocation point details have been
      // fetched
      Promise.all([
        fetchApiJson(`/assets/?details=true&portfolio_id=${this.id}`),
        fetchApiJson(
          `/allocation-points/?details=true&portfolio_id=${this.id}`,
        ),
      ]),
    )
    .then((data) => {
      // Fill the metering points with the detailed information from the API
      // responses
      const [assetData, allocationPointData] = data
      const assets = useMeteringPointsStore().assetsByPortfolio[this.id] ?? []
      fillMeteringPointDetails(assets, assetData)
      const allocationPoints =
        useMeteringPointsStore().allocationPointsByPortfolio[this.id] ?? []
      fillMeteringPointDetails(allocationPoints, allocationPointData)
      this.detailsLoaded = true
      onSuccess?.(data)
    })
    .catch((error) => {
      this.detailsLoaded = false
      onFail?.(error)
    })
}

function loadStatus(onSuccess, onFail) {
  fetchApiJson(`${this.endpoint}/status/`)
    .then((data) => {
      this.status = data
      onSuccess?.(data)
    })
    .catch((error) => {
      this.status = null
      onFail?.(error)
    })
}

function loadMeasurements(freq, dateRange, getExport, onSuccess, onFail) {
  const measurementTypes = this.type === "Battery" ? "power,soc" : "power"
  const endpoint = `${this.endpoint}/measurements/${getExport ? "export/" : ""}?measurement_type=${measurementTypes}`
  if (getExport) {
    dataFetch(endpoint, freq, dateRange, onSuccess, onFail)
  } else {
    cachedDataFetch(
      endpoint,
      this.measurements,
      freq,
      dateRange,
      onSuccess,
      onFail,
    )
  }
}

function loadBatteryCycles(dateRange, onSuccess, onFail) {
  const params = new URLSearchParams({
    date_start: format(dateRange[0], "yyyy-MM-dd"),
    date_end: format(addDays(dateRange[1], 1), "yyyy-MM-dd"),
  }).toString()
  fetchApiJson(`${this.endpoint}/kpis/battery-cycles/?${params}`)
    .then((data) => {
      onSuccess?.(data)
    })
    .catch((error) => {
      onFail?.(error)
    })
}

function loadRecords(freq, dateRange, getExport, onSuccess, onFail) {
  const endpoint = `/records/${this.id}/${getExport ? "export/" : ""}`
  if (getExport) {
    dataFetch(endpoint, freq, dateRange, onSuccess, onFail)
  } else {
    cachedDataFetch(endpoint, this.records, freq, dateRange, onSuccess, onFail)
  }
}

async function loadRecordStreams(
  freq,
  dateRange,
  getExport,
  onSuccess,
  onFail,
) {
  // Fetch collections first
  if (!this.collections) {
    try {
      this.collections = await fetchApiJson(
        `${this.endpoint}/record-stream-collections/`,
      )
    } catch (error) {
      onFail?.(error)
      return
    }
  }
  // Fetch record streams from first collection
  const uid = this.collections[0]?.collection_id
  if (!uid) {
    onFail?.()
    return
  }
  const endpoint = `/record-streams/${uid}/${getExport ? "export/" : ""}?format_as_records=true`
  if (getExport) {
    dataFetch(endpoint, freq, dateRange, onSuccess, onFail)
  } else {
    cachedDataFetch(
      endpoint,
      this.recordStreams,
      freq,
      dateRange,
      onSuccess,
      onFail,
    )
  }
}

function loadResults(freq, dateRange, getExport, onSuccess, onFail) {
  if (dateRange[0] < config.LEGACY_RESULTS_BREAKPOINT) {
    return this.loadRecordStreams(freq, dateRange, getExport, onSuccess, onFail)
  }
  return this.loadRecords(freq, dateRange, getExport, onSuccess, onFail)
}

function loadOptimaBenefit(dateRange, onSuccess, onFail) {
  const endpoint = `/records/${this.id}/optima-benefit/`
  cachedDataFetch(
    endpoint,
    this.optimaBenefit,
    null,
    dateRange,
    onSuccess,
    onFail,
  )
}

function loadFcrAllocations(dateRange, onSuccess, onFail) {
  const endpoint = `${this.endpoint}/fcr-allocations/`
  cachedDataFetch(
    endpoint,
    this.fcrAllocations,
    null,
    dateRange,
    onSuccess,
    onFail,
  )
}

function loadAfrrActivations(dateRange, onSuccess, onFail) {
  const endpoint = `${this.endpoint}/afrr-activations/`
  cachedDataFetch(
    endpoint,
    this.afrrActivations,
    null,
    dateRange,
    onSuccess,
    onFail,
  )
}

function loadSetpoints(dateRange, onSuccess, onFail) {
  const endpoint = `${this.endpoint}/setpoints/?include_default=true`
  cachedDataFetch(endpoint, this.setpoints, null, dateRange, onSuccess, onFail)
}

function loadEnergyHub(freq, dateRange, onSuccess, onFail) {
  const endpoint = `${this.endpoint}/energy-hub/`
  cachedDataFetch(endpoint, this.energyHub, freq, dateRange, onSuccess, onFail)
}

/* ---------------------------- Store definitions --------------------------- */

const baseGetters = {
  id: (state) => state.properties?.id,
  name: (state) => state.properties?.name,
  demo: (state) => state.properties?.demo,
  alias,
  displayName,
}

const baseActions = {
  setMeteringPoint,
  loadRecords,
  loadRecordStreams,
  loadResults,
}

export function usePortfolioStoreFactory(id) {
  return defineStore(`PORTFOLIO-${id}`, {
    state: () => ({
      role: PORTFOLIO,
      endpoint: `/portfolios/${id}`,
      properties: {},
      records: makeDataFetchCache(),
      recordStreams: makeDataFetchCache(),
      energyHubAPs: [],
      collections: null,
      detailsLoaded: false,
    }),
    getters: {
      ...baseGetters,
      queryString,
    },
    actions: {
      ...baseActions,
      loadPortfolioDetails,
    },
  })() // <-- Note the () here to call the function
}

export function useAllocationPointStoreFactory(id) {
  return defineStore(`ALLOCATION-POINT-${id}`, {
    state: () => ({
      role: ALLOCATION_POINT,
      endpoint: `/allocation-points/${id}`,
      properties: {},
      records: makeDataFetchCache(),
      recordStreams: makeDataFetchCache(),
      optimaBenefit: makeDataFetchCache(),
      energyHub: makeDataFetchCache(),
      collections: null,
    }),
    getters: {
      ...baseGetters,
      portfolioId: (state) => state.properties?.portfolio_id,
    },
    actions: {
      ...baseActions,
      loadOptimaBenefit,
      loadEnergyHub,
    },
  })() // <-- Note the () here to call the function
}

export function useAssetStoreFactory(id) {
  return defineStore(`ASSET-${id}`, {
    state: () => ({
      role: ASSET,
      endpoint: `/assets/${id}`,
      properties: {},
      status: null,
      records: makeDataFetchCache(),
      recordStreams: makeDataFetchCache(),
      optimaBenefit: makeDataFetchCache(),
      measurements: makeDataFetchCache(),
      fcrAllocations: makeDataFetchCache(),
      afrrActivations: makeDataFetchCache(),
      setpoints: makeDataFetchCache(),
    }),
    getters: {
      ...baseGetters,
      portfolioId: (state) => state.properties?.portfolio_id,
      type: (state) => state.properties?.type,
      allocationPointId: (state) => state.properties?.allocation_point_id,
      icon: (state) => ASSET_TYPE_ICONS[state.properties?.type] ?? "question",
    },
    actions: {
      ...baseActions,
      loadStatus,
      loadMeasurements,
      loadBatteryCycles,
      loadOptimaBenefit,
      loadFcrAllocations,
      loadSetpoints,
      loadAfrrActivations,
    },
  })() // <-- Note the () here to call the function
}
